import { HEN_BREEDS } from '../data/breeds';
import { ROOSTER_BREEDS } from '../data/roosters';
import { GATHER_TIERS } from '../data/farm';
import { UPGRADES_BY_ID } from '../data/upgrades';
import {
  BASE_CLICK_GATHER, BUFF_CAP, COMBO_CLICK_STEP, COMBO_VALUE_STEP, EVENTS, FLOCK_TAX, MAX_LAY_RATE,
  NEST_BASE_CAPACITY, NEST_BOX_COSTS, NEST_MAX_CAPACITY, NEST_UPGRADE_COSTS, OFFLINE_BASE_CAP_HOURS,
  OFFLINE_CAP_HOURS_PER_ROOSTER, ROOSTER_COSTS, ROOSTER_SLOT_HENS, STARTER_BOXES, VOUCHER_DISCOUNT, VOUCHER_MAX,
} from '../data/balance';
import type { BreedId, GatherTierDef, InfraDef, UpgradeDef } from '../data/types';
import { buffProduct } from './buffs';
import { globalAdd, globalMul, henMods } from './modifiers';
import { countBoxes, countHens, countRoosters, flockIndex } from './state';
import type { Flock, GameState, Hen, NestBox, SimCtx } from './types';

/**
 * The ONE income model (digest §4.6). Every number the player sees — card
 * $/s, header income, offline earnings, event payouts — comes from here.
 */

// ── Per-hen stats ─────────────────────────────────────────────────────

export interface HenStats {
  /** Eggs per second (0 while broody). */
  layRate: number;
  /** $ per egg before collection-stream multipliers. */
  eggValue: number;
  /** Multiplier for eggs collected by clicking. */
  clickMult: number;
  /** Multiplier for eggs collected by auto-gathering. */
  autoMult: number;
}

function layBuff(s: GameState, flock: Flock): number {
  return Math.min(BUFF_CAP, buffProduct(s.buffs.lay, s.time) * buffProduct(flock.crowBuffs, s.time));
}

function transientValue(s: GameState): number {
  const rotten = s.time < s.debuffUntil ? EVENTS.rotten.debuffValue : 1;
  return rotten * (1 + s.combo.tier * COMBO_VALUE_STEP);
}

/** Full stats for one hen. `steady` = ignore transient buffs (for payouts and displays of base income). */
export function henStats(s: GameState, ctx: SimCtx, flock: Flock, hen: Hen, steady = false): HenStats {
  const breed = HEN_BREEDS[hen.breed];
  const m = henMods(s, ctx, hen);
  const broody = hen.broodySince !== null;
  const lay = broody ? 0 : Math.min(MAX_LAY_RATE, breed.layRate * m.lay * (steady ? 1 : layBuff(s, flock)));
  const value = (breed.eggValue + m.valueFlat) * m.value * (steady ? 1 : transientValue(s));
  const click = m.clickValue * (steady ? 1 : (1 + s.combo.tier * COMBO_CLICK_STEP) * buffProduct(s.buffs.click, s.time));
  const auto = m.autoValue * gatherValueBonus(s) * (steady ? 1 : buffProduct(s.buffs.auto, s.time));
  return { layRate: lay, eggValue: value, clickMult: click, autoMult: auto };
}

// ── Farm totals ───────────────────────────────────────────────────────

export interface FarmTotals {
  /** Eggs laid per second across the farm. */
  layRate: number;
  /** $ per second of eggs produced (egg value only, no collection bonuses). */
  production: number;
  /** Auto-gather capacity in eggs per second. */
  gatherRate: number;
  /** Fraction of laid eggs the gatherers can keep up with (0..1). */
  efficiency: number;
  /** $ per second earned with no clicking at all. */
  passiveIncome: number;
}

export function farmTotals(s: GameState, ctx: SimCtx, steady = false): FarmTotals {
  let layRate = 0;
  let production = 0;
  let autoProduction = 0;
  for (const f of s.flocks) {
    for (const b of f.boxes) {
      if (!b.hen) continue;
      const st = henStats(s, ctx, f, b.hen, steady);
      layRate += st.layRate;
      production += st.layRate * st.eggValue;
      autoProduction += st.layRate * st.eggValue * st.autoMult;
    }
  }
  const gRate = gatherRate(s, ctx, steady);
  const efficiency = layRate > 0 ? Math.min(1, gRate / layRate) : 0;
  return { layRate, production, gatherRate: gRate, efficiency, passiveIncome: autoProduction * efficiency };
}

/** "Seconds of income" basis for event and critter payouts: steady production. */
export function payoutBasis(s: GameState, ctx: SimCtx): number {
  return farmTotals(s, ctx, true).production;
}

// ── Gathering ─────────────────────────────────────────────────────────

export function gatherRate(s: GameState, ctx: SimCtx, steady = false): number {
  let rate = 0;
  for (const def of GATHER_TIERS) rate += def.rate * (s.gather[def.id] ?? 0);
  rate *= globalMul(s, ctx, 'gatherRate');
  if (!steady) rate *= buffProduct(s.buffs.gather, s.time);
  return rate;
}

export function bestGatherTier(s: GameState): GatherTierDef | null {
  let best: GatherTierDef | null = null;
  for (const def of GATHER_TIERS) if ((s.gather[def.id] ?? 0) > 0 && (!best || def.tier > best.tier)) best = def;
  return best;
}

export function gatherValueBonus(s: GameState): number {
  return bestGatherTier(s)?.valueBonus ?? 1;
}

export function clickGather(s: GameState): number {
  let n = BASE_CLICK_GATHER;
  for (const id of s.upgrades) {
    const g = UPGRADES_BY_ID.get(id)?.clickGather;
    if (g !== undefined && g > n) n = g;
  }
  return n;
}

// ── Nests ─────────────────────────────────────────────────────────────

export function nestCapacity(box: NestBox): number {
  return Math.min(NEST_MAX_CAPACITY, NEST_BASE_CAPACITY + box.capacityLevel);
}

export function nestUpgradeCost(box: NestBox): number | null {
  if (nestCapacity(box) >= NEST_MAX_CAPACITY) return null;
  return NEST_UPGRADE_COSTS[Math.min(box.capacityLevel, NEST_UPGRADE_COSTS.length - 1)]!;
}

// ── Prices ────────────────────────────────────────────────────────────

export function flockTax(s: GameState, flockId: number): number {
  return Math.pow(FLOCK_TAX, flockIndex(s, flockId));
}

export function nestBoxCost(s: GameState, flockId: number): number {
  const bought = Math.max(0, countBoxes(s) - STARTER_BOXES);
  const base = NEST_BOX_COSTS[Math.min(bought, NEST_BOX_COSTS.length - 1)]!;
  return Math.floor(base * flockTax(s, flockId));
}

export function henCost(s: GameState, breed: BreedId, flockId: number): number {
  return Math.floor(HEN_BREEDS[breed].cost * flockTax(s, flockId));
}

export function upgradeCost(s: GameState, def: UpgradeDef, flockId?: number): number {
  return def.category === 'coop' && flockId !== undefined ? Math.floor(def.cost * flockTax(s, flockId)) : def.cost;
}

export function infraCost(s: GameState, def: InfraDef): number {
  return Math.floor(def.baseCost * Math.pow(def.costGrowth, s.infra[def.id] ?? 0));
}

/** Price of the next rooster after vouchers, or Infinity at the 10-rooster cap. */
export function roosterCost(s: GameState): number {
  const n = countRoosters(s);
  const base = ROOSTER_COSTS[n];
  if (base === undefined) return Infinity;
  return Math.floor(base * (1 - Math.min(VOUCHER_MAX, s.vouchers) * VOUCHER_DISCOUNT));
}

export interface RoosterSlots {
  open: number;
  roosters: number;
  /** Total hens needed for the next slot, or null when all 10 are unlocked. */
  nextAtHens: number | null;
}

export function roosterSlots(s: GameState): RoosterSlots {
  const hens = countHens(s);
  const roosters = countRoosters(s);
  const unlocked = ROOSTER_SLOT_HENS.filter((t) => hens >= t).length;
  const next = ROOSTER_SLOT_HENS.find((t) => hens < t) ?? null;
  return { open: Math.max(0, unlocked - roosters), roosters, nextAtHens: roosters < ROOSTER_SLOT_HENS.length ? next : null };
}

// ── Roosters & crows ──────────────────────────────────────────────────

export interface CrowStats {
  power: number;
  durationMs: number;
  cooldownMs: number;
}

export function crowStats(s: GameState, ctx: SimCtx, breed: BreedId): CrowStats {
  const rb = ROOSTER_BREEDS[breed];
  return {
    power: 1 + (rb.crowPower - 1) * globalMul(s, ctx, 'crowPower'),
    durationMs: (rb.crowDurationSec + globalAdd(s, ctx, 'crowDuration')) * 1000 * globalMul(s, ctx, 'eventEffect'),
    cooldownMs: rb.crowCooldownSec * 1000 * globalMul(s, ctx, 'crowCooldown'),
  };
}

// ── Offline cap ───────────────────────────────────────────────────────

export function offlineCapMs(s: GameState, ctx: SimCtx): number {
  const hours = OFFLINE_BASE_CAP_HOURS + OFFLINE_CAP_HOURS_PER_ROOSTER * countRoosters(s) + globalAdd(s, ctx, 'offlineCapHours');
  return hours * 3_600_000;
}

/** Money guard: never let NaN/Infinity into the balance. */
export function safeAdd(current: number, delta: number): number {
  const next = current + delta;
  return Number.isFinite(next) ? next : current;
}

export function earn(s: GameState, amount: number): number {
  if (!(amount > 0) || !Number.isFinite(amount)) return 0;
  s.money = safeAdd(s.money, amount);
  s.lifetimeEarned = safeAdd(s.lifetimeEarned, amount);
  return amount;
}

export function spend(s: GameState, amount: number): boolean {
  if (!Number.isFinite(amount) || amount < 0 || s.money < amount) return false;
  s.money -= amount;
  return true;
}
